import {
  Autocomplete,
  AutocompleteProps,
  CircularProgress,
  TextField,
} from "@mui/material";
import { useField } from "formik";
import { useRequest } from "hooks";
import _ from "lodash";
import { IGetAllProductRequest, IProduct } from "model";
import { useCallback, useState } from "react";
import { MasterDataApiService } from "services";
import { I18nTranslator, translations } from "utils/i18n";

interface IProductSelectProps
  extends Pick<
    AutocompleteProps<TProduct, undefined, undefined, undefined>,
    "defaultValue" | "disabled"
  > {
  onProductSelected?: (product: TProduct | null) => void;
  additionalLabel?: string;
  required?: boolean
}
export const ProductCodeSelect = ({
  onProductSelected,
  defaultValue,
  disabled,
  additionalLabel,
  required = true
}: IProductSelectProps) => {
  const [_value, setValue] = useState<TProduct | undefined | null>(
    defaultValue
  );
  const [isProductSelectOpen, setIsProductSelectOpen] =
    useState<boolean>(false);
  const [getAllProductsRequest, setGetAllProductsRequest] =
    useState<IGetAllProductRequest>({
      pageNumber: 1,
      pageSize: 100,
    });
  const { data: products, loading: isLoadingProduct } = useRequest<
    IGetAllProductRequest,
    IProduct[]
  >(getAllProductsRequest, MasterDataApiService.getAllProducts);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [__, meta, helper] = useField("productId");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setGetAllProductsSearchText = useCallback<(searchText: string) => void>(
    _.debounce((searchText: string) => {
      setGetAllProductsRequest((prev) => ({
        ...prev,
        searchText: searchText,
      }));
    }, 500),
    []
  );

  return (
    <Autocomplete<TProduct>
      id="get-all-product"
      open={isProductSelectOpen}
      onOpen={() => {
        setIsProductSelectOpen(true);
      }}
      onClose={() => {
        setIsProductSelectOpen(false);
      }}
      isOptionEqualToValue={(option, value) =>
        option.id === value.id || option.productCode === value.productCode
      }
      getOptionLabel={(option) =>
        option.productCode
          ? `${option.productCode!} ${
              option.description
                ? `| ${option.description}`
                : additionalLabel || ""
            }`
          : ""
      }
      options={products || []}
      filterOptions={(x) => x}
      loading={isLoadingProduct}
      onChange={(_, value) => {
        helper.setValue(value?.id);
        setValue(value);
        onProductSelected?.(value);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={I18nTranslator(
            translations.inProcessCheckReportDetail.productCode
          )}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoadingProduct ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          required={required}
          error={meta.touched && !_.isEmpty(meta.error)}
          helperText={meta.touched && meta.error && I18nTranslator(meta.error)}
        />
      )}
      onInputChange={(__, newInputValue) => {
        setGetAllProductsSearchText(newInputValue.split("|")[0].trim());
      }}
      defaultValue={defaultValue}
      value={_value}
      disabled={disabled}
    />
  );
};

type TProduct = Pick<
  Partial<IProduct>,
  "id" | "productCode" | "groupId" | "description"
>;
